import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { APP_CONFIG } from '../../config/appConfig';
import { setCredentials } from '../../store/slices/authSlice';
import { STUDENT_ROSTER } from '../../constants/studentRoster';

type LoginMode = 'teacher' | 'student' | 'unknown';

const resolveMode = (pathname: string): LoginMode => {
  if (pathname.startsWith('/teacher/login')) {
    return 'teacher';
  }

  if (pathname.startsWith('/student/login')) {
    return 'student';
  }

  return 'unknown';
};

const shellClassName =
  'mx-auto w-full max-w-[30rem] rounded-[2rem] border border-stone-200 bg-white/95 p-8 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur sm:p-10';

const fieldClassName =
  'block w-full rounded-[1.6rem] border border-stone-300 bg-white px-6 py-5 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900';

export const Login: React.FC = () => {
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [studentNumber, setStudentNumber] = useState('');
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogo, setShowLogo] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const mode = resolveMode(location.pathname);

  useEffect(() => {
    setError('');
    setIsSubmitting(false);
  }, [location.pathname]);

  useEffect(() => {
    document.title = APP_CONFIG.appName;
  }, []);

  const studentSuggestions = useMemo(() => {
    const keyword = displayName.trim();

    if (!keyword) {
      return [];
    }

    return STUDENT_ROSTER.filter((student) => student.displayName.includes(keyword)).slice(0, 8);
  }, [displayName]);

  const handleTeacherSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await api.post('/auth/teacher-login', {
        account: account.trim(),
        password,
      });
      const { user, accessToken, refreshToken } = response.data;
      dispatch(setCredentials({ user, token: accessToken, refreshToken: refreshToken ?? null }));
      navigate('/teacher');
    } catch (err) {
      console.error(err);
      setError('登录失败，请检查教师账号和密码');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStudentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await api.post('/auth/student-login', {
        displayName: displayName.trim(),
        studentNumber: studentNumber.trim(),
      });
      const { user, accessToken, refreshToken } = response.data;
      dispatch(setCredentials({ user, token: accessToken, refreshToken: refreshToken ?? null }));
      navigate('/student');
    } catch (err) {
      console.error(err);
      setError('登录失败，请确认姓名和学号是否匹配');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === 'unknown') {
    return <Navigate to="/student/login" replace />;
  }

  if (mode === 'teacher') {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,#f8fafc_0%,#f5f5f4_50%,#ede7dd_100%)] px-4 py-12 text-slate-900 sm:px-6">
        <div className="mx-auto flex min-h-[calc(100vh-6rem)] max-w-5xl items-center justify-center">
          <form className={shellClassName} onSubmit={handleTeacherSubmit}>
            <div className="mb-8 flex flex-col items-center text-center">
              {showLogo && (
                <img
                  src="/site-logo.png"
                  alt={`${APP_CONFIG.appName} Logo`}
                  className="mb-5 h-24 w-24 rounded-[1.75rem] object-cover shadow-lg"
                  onError={() => setShowLogo(false)}
                />
              )}
              <h1 className="text-5xl font-black tracking-tight text-slate-900">
                {APP_CONFIG.appName}
              </h1>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                required
                className={fieldClassName}
                placeholder="请输入教师账号"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
              />
              <input
                type="password"
                required
                className={fieldClassName}
                placeholder="请输入教师密码"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && <div className="mt-4 text-sm text-rose-500">{error}</div>}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-8 w-full rounded-[1.7rem] bg-slate-900 px-6 py-5 text-xl font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {isSubmitting ? '登录中...' : '进入教师端'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/student/login')}
              className="mt-4 w-full rounded-[1.7rem] border border-stone-300 bg-white px-6 py-4 text-base font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
            >
              切换到学生端登录
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#f6fbf8_0%,#f0f7f3_45%,#e6efe9_100%)] px-4 py-12 text-slate-900 sm:px-6">
      <div className="mx-auto flex min-h-[calc(100vh-6rem)] max-w-5xl items-center justify-center">
        <form className={shellClassName} onSubmit={handleStudentSubmit}>
          <div className="mb-8 flex flex-col items-center text-center">
            {showLogo && (
              <img
                src="/site-logo.png"
                alt={`${APP_CONFIG.appName} Logo`}
                className="mb-5 h-24 w-24 rounded-[1.75rem] object-cover shadow-lg"
                onError={() => setShowLogo(false)}
              />
            )}
            <h1 className="text-5xl font-black tracking-tight text-slate-900">
              {APP_CONFIG.appName}
            </h1>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <input
                type="text"
                required
                className={fieldClassName}
                placeholder="请输入学生姓名"
                value={displayName}
                onFocus={() => setIsSuggestionOpen(true)}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setDisplayName(nextValue);
                  setIsSuggestionOpen(nextValue.trim().length > 0);
                }}
              />
              {isSuggestionOpen && studentSuggestions.length > 0 && (
                <div className="max-h-56 overflow-y-auto rounded-[1.6rem] border border-stone-200 bg-white">
                  {studentSuggestions.map((student, index) => (
                    <button
                      key={student.studentNumber}
                      type="button"
                      className={`block w-full px-6 py-4 text-left text-lg text-slate-700 transition hover:bg-emerald-50 ${
                        index !== studentSuggestions.length - 1 ? 'border-b border-stone-100' : ''
                      }`}
                      onClick={() => {
                        setDisplayName(student.displayName);
                        setIsSuggestionOpen(false);
                      }}
                    >
                      {student.displayName}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="text"
              inputMode="numeric"
              required
              className={fieldClassName}
              placeholder="请输入学号"
              value={studentNumber}
              onChange={(e) => setStudentNumber(e.target.value.replace(/[^\d]/g, ''))}
            />
          </div>

          {error && <div className="mt-4 text-sm text-rose-500">{error}</div>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-8 w-full rounded-[1.7rem] bg-emerald-700 px-6 py-5 text-xl font-bold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-emerald-300"
          >
            {isSubmitting ? '登录中...' : '进入学生端'}
          </button>
        </form>
      </div>
    </div>
  );
};
